import React from 'react'
import i18next from 'i18next'
import hash from 'object-hash'
import { Button, Modal, Form, Card } from 'react-bootstrap'
import ResponsiveTable from 'src/components/system_wide/responsive_table'
import SearchButton from 'src/components/system_wide/search_button'
import { Trans } from 'react-i18next'
import IconButton from './icon_button'
import FilterGroup from './filter_group'
import CheckboxFilter from './checkbox_filter'

const { t } = i18next

class SelectDealersModal extends React.Component {
  static defaultProps = {
    readonly: false,
    hideColumns: [],
    onChange: () => {},
    dealers: [],
    brands: [],
    multiSelection: false,
  }

  state = {
    show: false,
    selected_dealers: [],
    filtered_brands: [],
    search_filter: '',
    sortData: { sort_column: '', sort_direction: '' },
    filters_hash: '',
  }

  initialFiltersHash = ''

  show = ({ selected_dealers }) => {
    this.setState(
      {
        show: true,
        selected_dealers,
        filtered_brands: this.props.brands.map(b => b.id),
      },
      () => {
        const { filtered_brands } = this.state
        filtered_brands.sort()
        this.initialFiltersHash = hash({ filtered_brands })
        this.updateFiltersHash()
      }
    )
  }

  hide = () => {
    this.setState({
      show: false,
      selected_dealers: [],
      search_filter: '',
      filtered_brands: [],
      filters_hash: '',
    })
    this.initialFiltersHash = ''
  }

  updateFiltersHash = () => {
    let { filtered_brands } = this.state
    filtered_brands.sort()
    this.setState({ filters_hash: hash({ filtered_brands }) })
  }

  toggleDealer = ({ target: { value } }) => {
    const dealerId = parseInt(value)
    const { selected_dealers } = this.state

    if (this.props.multiSelection) {
      const newSelection = selected_dealers.includes(dealerId)
        ? selected_dealers.filter(id => id !== dealerId)
        : [...selected_dealers, dealerId]
      this.setState({ selected_dealers: newSelection })
    } else {
      this.setState({ selected_dealers: [dealerId] })
    }
  }

  toggleAllDealers = () => {
    let { selected_dealers } = this.state
    let dealers = this.searchFilterDealers()

    let allSelected = dealers.length > 0 && dealers.every(d => selected_dealers.includes(d.id))
    let current_selection = allSelected
      ? selected_dealers.filter(id => !dealers.find(d => d.id === id))
      : [...selected_dealers, ...dealers.filter(d => !selected_dealers.includes(d.id)).map(d => d.id)]

    this.setState({ selected_dealers: current_selection })
  }

  selectDealerCheckbox = ({ rowData }) => {
    let { selected_dealers } = this.state

    return (
      <Form.Check
        type={this.props.multiSelection ? 'checkbox' : 'radio'}
        className="px-2"
        value={rowData.dealer_brand.id}
        checked={selected_dealers.includes(parseInt(rowData.dealer_brand.id))}
        onChange={this.toggleDealer}
        disabled={this.props.readonly}
      />
    )
  }

  onChangeSearchFilter = filter => {
    this.setState({ search_filter: filter.replace(/[.*?^$]+/, '') })
  }

  onChangeFilter = name => values => {
    this.setState({ [name]: values }, () => this.updateFiltersHash())
  }

  onResetFilters = () => {
    this.setState(
      {
        filtered_brands: this.props.brands.map(b => b.id),
      },
      () => {
        this.updateFiltersHash()
      }
    )
  }

  searchFilterDealers = () => {
    const { dealers } = this.props
    const { search_filter, filtered_brands, sortData } = this.state

    const regexp = new RegExp(`.*${search_filter ? search_filter : ''}.*`, 'i')
    let filtered = dealers.filter(d => {
      const matchBrand = filtered_brands.includes(d.dealer_brand?.brand_id)
      const matchText = regexp.test(d.business_name) || regexp.test(d.piva) || regexp.test(d.address)
      return matchBrand && matchText
    })

    const { sort_column, sort_direction } = sortData
    if (sort_column) {
      filtered.sort((a, b) => {
        let valA = a[sort_column] || ''
        let valB = b[sort_column] || ''
        if (typeof valA === 'string') {
          valA = valA.toLowerCase()
          valB = valB.toLowerCase()
        }
        if (valA < valB) return sort_direction === 'ASC' ? -1 : 1
        if (valA > valB) return sort_direction === 'ASC' ? 1 : -1
        return 0
      })
    }

    return filtered
  }

  getData = (_index = 0, _chunkSize, sortData) => {
    this.setState({ sortData })
  }

  handleConfirm = () => {
    const selected = this.props.dealers.filter(d => this.state.selected_dealers.includes(d.dealer_brand.id))
    this.props.onChange(selected)
    this.hide()
  }

  onRowClick = rowData => {
    if (this.props.readonly) return
    const event = { target: { value: rowData.id.toString() } }
    this.toggleDealer(event)
  }

  renderDetails = rowData => {
    const { business_name, piva, address, agent, dealer_brand } = rowData
    let { hideColumns } = this.props

    return (
      <Card className="shadow-lg mb-4 border-0 rounded-lg">
        <Card.Body className="text-center pb-2 pt-2">
          <h4 className="fw-bold text-primary d-flex align-items-center justify-content-center">
            {this.selectDealerCheckbox({ rowData })} {business_name}
          </h4>
          <p className="text-muted">{piva}</p>

          {address && !hideColumns.includes('address') && (
            <h6 className="fw-semibold mb-1 d-flex justify-content-center align-items-center">{address}</h6>
          )}

          {agent && !hideColumns.includes('agent') && (
            <p className="text-muted mb-3">
              <i className="fas fa-user me-2"></i>
              {agent.first_name} {agent.last_name}
            </p>
          )}

          <div className="mb-3">{dealer_brand?.brand?.name ?? ''}</div>
        </Card.Body>
      </Card>
    )
  }

  getColumns = () =>
    [
      {
        title: this.props.multiSelection && (
          <IconButton
            icon="far fa-check-square me-0"
            tooltip={t('components.dealers.select_all_tooltip', 'Seleziona/Deseleziona Tutti')}
            onClick={this.toggleAllDealers}
          />
        ),
        className: 'd-none d-sm-block text-center',
        style: { width: '5%' },
        data: this.selectDealerCheckbox,
      },
      {
        title: t('components.dealers.business_name', 'Nome Azienda'),
        className: 'd-none d-sm-block flex-fill',
        style: { width: '30%' },
        sortable: true,
        sortingKey: 'business_name',
        data: ({ rowData: { business_name, piva } }) => (
          <div>
            <div className="fw-bold">{business_name}</div>
            <div>{piva}</div>
          </div>
        ),
      },
      {
        name: 'address',
        title: t('components.dealers.address', 'Indirizzo'),
        className: 'd-none d-sm-block text-center',
        style: { width: '30%' },
        sortable: true,
        sortingKey: 'address',
        data: 'address',
      },
      {
        name: 'agent',
        title: t('components.dealers.agent', 'Funzionario'),
        className: 'd-none d-sm-block text-center',
        style: { width: '20%' },
        data: ({ rowData: { agent } }) => agent?.first_name + ' ' + agent?.last_name,
      },
      {
        title: t('components.dealers.brand', 'Brand'),
        className: 'd-none d-sm-block text-center',
        style: { width: '15%' },
        data: ({ rowData: { dealer_brand } }) => dealer_brand?.brand?.name ?? '',
      },
      {
        title: t('components.dealers.details', 'Dettagli'),
        className: 'd-block d-sm-none flex-fill',
        style: { width: '100%' },
        sortable: true,
        data: ({ rowData }) => this.renderDetails(rowData),
      },
    ].filter(c => !this.props.hideColumns.includes(c.name))

  render() {
    const { show, search_filter, filtered_brands, filters_hash } = this.state
    const { readonly, brands } = this.props

    const filteredDealers = this.searchFilterDealers()

    return (
      <Modal backdrop="static" centered show={show} onHide={this.hide} size="xl">
        <Modal.Header>
          <Modal.Title>
            <Trans i18nKey="components.dealers.select_dealer_modal.title">Seleziona dealer</Trans>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column" style={{ height: '60vh' }}>
          <div className="d-flex flex-row mb-3">
            <div className="flex-fill">
              <div className="toolbar px-2 flex-nowrap">
                {brands.length > 0 && (
                  <FilterGroup
                    className="toolbar-item"
                    showResetFilters={this.initialFiltersHash !== filters_hash}
                    onResetFilters={this.onResetFilters}>
                    <CheckboxFilter
                      label={t('filter_brands', 'Brand')}
                      items={brands}
                      filteredItems={filtered_brands}
                      onChangeFilter={this.onChangeFilter('filtered_brands')}
                      showSearch={true}
                    />
                  </FilterGroup>
                )}
                <div className="toolbar-spacer"></div>
                <SearchButton className="float-md-end" value={search_filter} onChange={this.onChangeSearchFilter} />
              </div>
            </div>
          </div>

          <ResponsiveTable
            className="flex-fill"
            data={filteredDealers}
            columns={this.getColumns()}
            getData={this.getData}
            totalRecords={filteredDealers.length}
            hideStatusBar={true}
            onRowClick={!readonly && this.onRowClick}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.hide} variant="secondary">
            <Trans i18nKey="buttons.cancel">Annulla</Trans>
          </Button>
          <Button onClick={this.handleConfirm} variant="primary">
            <Trans i18nKey="buttons.confirm">Conferma</Trans>
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default SelectDealersModal
